import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Card from '@wtag/rcl-card';
import Alert from '@wtag/rcl-alert';
import ContentLoaderPlaceholder from '@wtag/rcl-content-loader-placeholder';
import ExchangePayable from './ExchangePayable';
import ExchangeCartSummaryAccordion from './ExchangeCartSummaryAccordion';
import ExchangePenalty from './ExchangePenalty';
import NewFlightSubtotal from './NewFlightSubtotal';
import NewFlightAccordionHeader from './NewFlightAccordionHeader';
import ExchangeAccordionContent from './ExchangeAccordionContent';
import OldFlightAccordionHeader from './OldFlightAccordionHeader';
import { FETCHED } from '../../reducers/flight/exchangeQuoteStatus';
import './styles.scss';

const ExchangeCartSummary = ({ exchangeQuoteData, trips, className, exchangeQuoteStatus }) => {
  const {
    totalNewPriceWithoutPenalty,
    exchangePenalty,
    totalNewPriceWithPenalty,
    alreadyPaidAmount,
    totalOldPriceWithoutPenalty,
    payableAmount,
    disablePriceBreakdown,
  } = exchangeQuoteData || {};

  return (
    <div className={classNames('col-lg-4 col-grid row-gap-20', className)}>
      <Card className="exchange-cart-summary">
        <div className="exchange-cart-summary__header">
          {I18n.t('components.ibe.exchange.cart.change_summary')}
        </div>
        {exchangeQuoteStatus === FETCHED || exchangeQuoteData ? (
          <Fragment>
            <ExchangePayable priceDetails={payableAmount} />
            {!disablePriceBreakdown && (
              <div className="exchange-cart-summary__content">
                <ExchangeCartSummaryAccordion
                  header={<NewFlightAccordionHeader priceDetails={totalNewPriceWithoutPenalty} />}
                >
                  <ExchangeAccordionContent trips={trips} price={totalNewPriceWithoutPenalty} />
                </ExchangeCartSummaryAccordion>
                <ExchangePenalty priceDetails={exchangePenalty} />
                <NewFlightSubtotal priceDetails={totalNewPriceWithPenalty} />
                <ExchangeCartSummaryAccordion
                  header={<OldFlightAccordionHeader priceDetails={alreadyPaidAmount} />}
                >
                  <ExchangeAccordionContent trips={trips} price={totalOldPriceWithoutPenalty} />
                </ExchangeCartSummaryAccordion>
              </div>
            )}
            <Alert
              className="exchange-cart-summary__alert"
              isVisible={true}
              type="warning-light"
              isBackgroundVisible={false}
              hideClose={true}
            >
              {I18n.t('components.ibe.exchange.cart.note')}
            </Alert>
            {!disablePriceBreakdown && <ExchangePayable priceDetails={payableAmount} />}
          </Fragment>
        ) : (
          <ContentLoaderPlaceholder
            className="exchange-cart-summary__loader"
            numberOfLines={10}
            showBackground={false}
          />
        )}
      </Card>
    </div>
  );
};

ExchangeCartSummary.defaultProps = {
  className: null,
  exchangeQuoteStatus: null,
  exchangeQuoteData: null,
};

ExchangeCartSummary.propTypes = {
  className: PropTypes.string,
  exchangeQuoteData: PropTypes.shape({
    newFlightPrice: PropTypes.shape({
      amount: PropTypes.string,
      currency: PropTypes.string,
    }),
    oldFlightPrice: PropTypes.shape({
      amount: PropTypes.string,
      currency: PropTypes.string,
    }),
    newTax: PropTypes.shape({
      amount: PropTypes.string,
      currency: PropTypes.string,
    }),
    oldTax: PropTypes.shape({
      amount: PropTypes.string,
      currency: PropTypes.string,
    }),
    totalNewPriceWithoutPenalty: PropTypes.shape({
      amount: PropTypes.string,
      currency: PropTypes.string,
    }),
    totalOldPriceWithoutPenalty: PropTypes.shape({
      amount: PropTypes.string,
      currency: PropTypes.string,
    }),
    totalNewPriceWithPenalty: PropTypes.shape({
      amount: PropTypes.string,
      currency: PropTypes.string,
    }),
    exchangePenalty: PropTypes.shape({
      amount: PropTypes.string,
      currency: PropTypes.string,
    }),
    alreadyPaidAmount: PropTypes.shape({
      amount: PropTypes.string,
      currency: PropTypes.string,
    }),
    payableAmount: PropTypes.shape({
      amount: PropTypes.string,
      currency: PropTypes.string,
    }),
    disablePriceBreakdown: PropTypes.bool,
  }),
  trips: PropTypes.arrayOf(
    PropTypes.shape({
      originCode: PropTypes.string,
      destinationCode: PropTypes.string,
    }),
  ).isRequired,
  travelerCount: PropTypes.shape({
    adults: PropTypes.arrayOf(PropTypes.shape({})),
    children: PropTypes.arrayOf(PropTypes.shape({})),
    infants: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  exchangeQuoteStatus: PropTypes.string,
};

export default ExchangeCartSummary;
